import {
  CoachMark,
  type CoachMarkArrow,
  placeCoachMark,
} from "@rosc/ui/v32/coach-mark";
import type { JSX } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { trackOnboarding } from "@/lib/guided/events";
import { cn } from "@/lib/utils";

// The guided note of a practice consulta: four stops, each skippable.
// 1. the flagged value (review card), 2. a line's source, 3. a fact-checked
// edit in the Ask bar (pre-filled), 4. Copy to chart. Each stop points at the
// real control, outlined; the card follows it as the page scrolls. While the
// review card is open the coach mark steps aside.

export type TourStop = "review" | "sources" | "ask" | "copy";
export const TOUR_STOPS: readonly TourStop[] = [
  "review",
  "sources",
  "ask",
  "copy",
];
const TARGETS: Record<TourStop, string[]> = {
  review: [
    '[data-testid="note-body"] [data-verify-index]',
    '[data-testid="needs-review"]',
  ],
  sources: [
    // A line whose every sentence has a source, so tapping it anywhere shows
    // what was said; else a line with at least one. Never a line without a
    // source (see sourcesTarget).
    '[data-testid="note-sources"] [data-source-supported="all"]',
    '[data-testid="note-sources"] [data-source-supported="some"]',
  ],
  ask: ['[data-testid="note-ask"]'],
  copy: ['[data-testid="copy-to-chart"]'],
};
const HIGHLIGHT = [
  "ring-2",
  "ring-v3-brand",
  "ring-offset-2",
  "ring-offset-v3-panel",
  "transition-shadow",
];
const PHONE = "(max-width: 639px)";
const TOAST_GAP = 8;

// How far to move the card up or down so it clears every toast on screen
// (toasts are top-centre on phones, bottom-centre above the Ask bar on
// desktop). 0 when nothing overlaps.
export const clearOfToasts = (
  card: { top: number; bottom: number; left: number; right: number },
  toasts: readonly {
    top: number;
    bottom: number;
    left: number;
    right: number;
  }[],
  viewportHeight: number,
): number => {
  let shift = 0;
  for (const toast of toasts) {
    const top = card.top + shift;
    const bottom = card.bottom + shift;
    const overlaps =
      top < toast.bottom + TOAST_GAP &&
      bottom > toast.top - TOAST_GAP &&
      card.left < toast.right &&
      card.right > toast.left;
    if (!overlaps) continue;
    const up = toast.top - TOAST_GAP - bottom;
    const down = toast.bottom + TOAST_GAP - top;
    const upFits = top + up >= TOAST_GAP;
    const downFits = bottom + down <= viewportHeight - TOAST_GAP;
    shift +=
      upFits && (!downFits || Math.abs(up) <= Math.abs(down)) ? up : down;
  }
  return Math.round(shift);
};

// Sources off for this note (no sources layer): any line, as before.
const SOURCES_OFF_TARGETS = [
  '[data-testid="note-body"] li',
  '[data-testid="note-body"]',
];

// The sources stop's target: a sourced line; null while the sources load;
// "none" when they loaded and no line of this note has a source (the stop is
// skipped rather than pointing at a line that opens "No source").
export const sourcesTarget = (): HTMLElement | null | "none" => {
  const layer = document.querySelector<HTMLElement>(
    '[data-testid="note-sources"]',
  );
  if (!layer) {
    for (const selector of SOURCES_OFF_TARGETS) {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) return element;
    }
    return null;
  }
  for (const selector of TARGETS.sources) {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) return element;
  }
  return layer.dataset["sources"] === "ready" ? "none" : null;
};

const find = (stop: TourStop): HTMLElement | null => {
  if (stop === "sources") {
    const target = sourcesTarget();
    return target === "none" ? null : target;
  }
  for (const selector of TARGETS[stop]) {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) return element;
  }
  return null;
};

// Desktop: the right pane (assistant, transcript) is a column beside the
// note; the card stays left of it so it never covers the proposal.
const noteRight = (): number => {
  const pane = document.querySelector<HTMLElement>(
    '[data-testid="assistant-panel"], [data-testid="side-pane"]',
  );
  const left = pane?.getBoundingClientRect().left ?? 0;
  return left > 0 ? Math.min(left, window.innerWidth) : window.innerWidth;
};

interface Placement {
  top: number;
  left: number;
  arrow: CoachMarkArrow;
  arrowOffset: number;
}

export const PracticeTour = ({
  open,
  reviewed,
  asked = false,
  paused = false,
  onPrefillAsk,
  onClose,
}: {
  open: boolean;
  /** The flagged value was confirmed or edited: stop 1 is done. */
  reviewed: boolean;
  /** The Ask bar edit was sent: stop 3 is done (the copy stop follows). */
  asked?: boolean;
  /**
   * Something covers the note (the assistant drawer or sheet): the card
   * steps aside and comes back at the same stop.
   */
  paused?: boolean;
  onPrefillAsk: (text: string) => void;
  /** Finished or skipped. */
  onClose: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const stop = TOUR_STOPS[step] ?? "review";
  const card = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [hidden, setHidden] = useState(false);
  // Moves the card clear of toasts while they show.
  const [toastShift, setToastShift] = useState(0);
  const shiftRef = useRef(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const [phone, setPhone] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(PHONE).matches,
  );

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);
  // Confirming the value moves on to the next stop.
  useEffect(() => {
    if (open && reviewed && stop === "review") setStep(1);
  }, [open, reviewed, stop]);
  // Sending the edit finishes the Ask stop; the guided ending (Copy to
  // chart) comes next, also while the assistant column shows the result.
  useEffect(() => {
    if (open && asked && stop === "ask") setStep(TOUR_STOPS.indexOf("copy"));
  }, [open, asked, stop]);
  useEffect(() => {
    if (!open) return;
    trackOnboarding("coach_step_viewed", { stop });
    if (stop === "ask") onPrefillAsk(t("guided.coach.ask.suggestion"));
    const target = find(stop);
    target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [open, stop, onPrefillAsk, t]);
  useEffect(() => {
    const query = window.matchMedia?.(PHONE);
    if (!query) return;
    const change = (): void => setPhone(query.matches);
    query.addEventListener?.("change", change);
    return () => query.removeEventListener?.("change", change);
  }, []);

  // Outline the target and keep the card next to it.
  useLayoutEffect(() => {
    if (!open) return;
    let frame = 0;
    let lit: HTMLElement | null = null;
    const tick = (): void => {
      // No line of this note has a source: go on to the next stop.
      if (stop === "sources" && sourcesTarget() === "none") {
        trackOnboarding("coach_step_skipped", { stop, reason: "no_source" });
        setStep(TOUR_STOPS.indexOf("sources") + 1);
        return;
      }
      const target = find(stop);
      if (target !== lit) {
        // A sourced line found once the sources loaded: bring it into view.
        if (target && !lit && stop === "sources")
          target.scrollIntoView?.({ block: "center", behavior: "smooth" });
        lit?.classList.remove(...HIGHLIGHT);
        target?.classList.add(...HIGHLIGHT);
        lit = target;
      }
      // The review card or a line's source is open: the card steps aside.
      setHidden(
        pausedRef.current ||
          !!document.querySelector(
            '[data-testid="review-card"], [data-testid="source-card"]',
          ),
      );
      const mark = document.querySelector<HTMLElement>(
        '[data-testid="practice-tour"]',
      );
      if (mark) {
        const rect = mark.getBoundingClientRect();
        const base = {
          top: rect.top - shiftRef.current,
          bottom: rect.bottom - shiftRef.current,
          left: rect.left,
          right: rect.right,
        };
        const toasts = Array.from(
          document.querySelectorAll<HTMLElement>(
            '[data-testid="toast-v3"], [data-testid="toast-v3-viewport"] > li',
          ),
        )
          .map((toast) => toast.getBoundingClientRect())
          .filter((box) => box.width > 0 && box.height > 0);
        const next = clearOfToasts(base, toasts, window.innerHeight);
        if (next !== shiftRef.current) {
          shiftRef.current = next;
          setToastShift(next);
        }
      }
      if (!target && card.current && !phone) {
        // The control is not on screen (yet): the card waits at the bottom.
        const width = card.current.offsetWidth;
        const height = card.current.offsetHeight;
        const next: Placement = {
          top: window.innerHeight - height - 24,
          left: Math.max(16, (noteRight() - width) / 2),
          arrow: "none",
          arrowOffset: 0,
        };
        setPlacement((current) =>
          current && current.top === next.top && current.left === next.left
            ? current
            : next,
        );
      }
      if (target && card.current && !phone) {
        const rect = target.getBoundingClientRect();
        const next = placeCoachMark({
          target: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          },
          card: {
            width: card.current.offsetWidth,
            height: card.current.offsetHeight,
          },
          viewport: { width: noteRight(), height: window.innerHeight },
        });
        setPlacement((current) =>
          current &&
          Math.abs(current.top - next.top) < 1 &&
          Math.abs(current.left - next.left) < 1 &&
          current.arrow === next.arrow
            ? current
            : next,
        );
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      lit?.classList.remove(...HIGHLIGHT);
    };
  }, [open, stop, phone]);

  if (!open || typeof document === "undefined") return null;
  const last = step >= TOUR_STOPS.length - 1;
  const finish = (skipped: boolean): void => {
    trackOnboarding(skipped ? "coach_skipped" : "coach_completed", { stop });
    onClose();
  };
  return createPortal(
    <div
      ref={card}
      className={cn(
        "v3-remap",
        phone ? "contents" : "fixed z-50",
        hidden && "invisible",
      )}
      style={
        phone
          ? undefined
          : {
              top: placement?.top ?? -9999,
              left: placement?.left ?? -9999,
            }
      }
    >
      <CoachMark
        arrow={phone || toastShift ? "none" : (placement?.arrow ?? "none")}
        arrowOffset={placement?.arrowOffset}
        style={
          toastShift ? { transform: `translateY(${toastShift}px)` } : undefined
        }
        className={cn(
          phone &&
            stop === "ask" &&
            "bottom-auto top-[max(1rem,env(safe-area-inset-top))]",
        )}
        closeLabel={t("guided.coach.close")}
        layout={phone ? "dock" : "card"}
        primaryLabel={last ? t("guided.coach.done") : t("guided.coach.next")}
        skipLabel={t("guided.coach.skip")}
        step={step}
        stepLabel={t("guided.coach.step", {
          current: step + 1,
          total: TOUR_STOPS.length,
        })}
        testId="practice-tour"
        title={t(`guided.coach.${stop}.title`)}
        total={TOUR_STOPS.length}
        onClose={() => finish(true)}
        onPrimary={() => {
          if (last) finish(false);
          else setStep(step + 1);
        }}
        onSkip={() => finish(true)}
      >
        {t(`guided.coach.${stop}.body`)}
      </CoachMark>
    </div>,
    document.body,
  );
};
